import $ from "jquery";
import ko from "lib/knockout";
import {ReviewItem} from "./reviewitem";
import {MODAL_REVIEW} from "core/modals";
import {siteUrl} from "core/utils/url";
import {Notifier} from "../../../../core/widgets/notifier";
import {InfiniteScroll} from "lib/zinfinitescroll";

export class ReviewList {
	reviews = ko.observableArray([]);
	users = ko.observableArray([]);

	reviewCols = ko.observableArray([]);
	colIndex = 0;

	_filters = null;

	/** @type {InfiniteScroll} */
	_infScroll = null;
	_page = 0;
	_loading = ko.observable(false);
	_xhr = null;
	_onLoad = $.Callbacks();
	_totalCount = null;
	_averageRating = null;

	constructor(data) {
		const o = this._o = {
			/**
			 * Wrapper element to enable infinite scroll plugin
			 */
			infScrollWrapper: null,
            /**
             * Infinite scroll plugin distance parameter
             */
            infScrollDistance: 2000,
			/**
			 * Render in columns or linear
			 */
			columns: true,
			columnsCount: 5,
            /**
			 * Calculate average rating on server
             */
            calculateAverageRating: false,
            /**
             * Show images
             */
            showImages: true,
			/**
			 * Show videos
			 */
			showVideos: false,
            /**
			 * Single review/question details
             */
            isDetails: false,
			/**
			 *  Include sales into list
			 */
			includeSales: false,

			...data
		};

		for(let i = 0; i < o.columnsCount; i++) {
			this.reviewCols.push(ko.observableArray([]));
		}

		if(o.infScrollWrapper) {
        	this._initInfScroll();
		}

		this._handleItemClick = this._handleItemClick.bind(this);
	}

	/**
	 * Render as columns
	 * @return {boolean}
	 */
	get columns() {
		return this._o.columns;
	}

	/**
	 * Number of columns
	 * @return {int}
	 */
	get columnsCount() {
		return this._o.columnsCount;
	}

	/**
	 * Is loading at the moment
	 * @return {boolean}
	 */
	get isLoading() {
		return this._loading();
	}

    /**
     * Total reviews count
     * @return {int}
     */
    get totalCount() {
        return this._totalCount;
    }

    /**
     * Average reviews rating
     * @return {int}
     */
    get averageRating() {
        return this._averageRating;
    }

    /**
     * Are images visible
     * @return {boolean}
     */
    get showImages() {
        return this._o.showImages;
    }

	/**
	 * Are videos visible
	 * @return {boolean}
	 */
	get showVideos() {
    	return this._o.showVideos;
	}

    /**
     * Are images visible
     * @return {boolean}
     */
    get isDetails() {
        return this._o.isDetails;
    }

	/**
	 * Loads reviews from server with given filters
	 * @param {object} filters
	 * @return {JQuery.jqXHR}
	 */
	load(filters) {
		this.clear();
		this._filters = filters;
		return this._loadReviews().done(response => {
			this._totalCount = response.totalCount;
			if(response.averageRating !== undefined) {
                this._averageRating = response.averageRating;
            }
			setTimeout(() => {
				if (this._infScroll) {
					this._infScroll.on();
				}
			});
		});
	}

    /**
	 * Sets reviews from array
     * @param {array} reviews
	 * @param {number} totalCount
     */
	loadFromArray(reviews, totalCount = null, filters = null) {
		this.clear();
        this._page = 1;

		// Model updates data internally so deep copy of data is created
		const reviewsCopy = [];
		reviews.forEach(review => reviewsCopy.push($.extend(true, {}, review)));

        const reviewItems = this._addItems(reviewsCopy);

        let hasMore = false;
        if(totalCount !== null) {
        	this._totalCount = totalCount;
        	if(totalCount > reviewItems.length) {
        		hasMore = true;
			}
		}

		if(filters !== null) {
        	this._filters = filters;
		}

        setTimeout(() => this._onLoad.fireWith(this, [reviewItems]));

        if(hasMore && this._infScroll) {
            setTimeout(() => this._infScroll.on());
		}

	}

	/**
	 * Clears everything
	 */
	clear() {
		this.reviews().forEach(item => item.offClick(this._handleItemClick));
		this.reviews([]);
		this.reviewCols().forEach(col => col([]));
		if (this._infScroll) {
			this._infScroll.off();
		}
		this.colIndex = 0;
		this._page = 0;
		this._filters = null;
		this._loading(false);
		if (this._xhr) {
			this._xhr.abort();
		}
	}

	enableSizWatcher() {
        $(window).on('resize', () => {
            ReviewList._reviewSizeSpot(true);
        });
		this.onLoad(ReviewList._reviewSizeSpot);
	}

	onLoad(handler) {
		this._onLoad.add(handler);
	}

	offLoad(handler) {
		this._onLoad.remove(handler);
	}

    /**
	 * @param {ReviewItem} item
     */
	deleteItem(item) {
        $.post(siteUrl('reviews/delete_review'),
            {id: item.id},
            response => {
                if(response.success) {
                    if(this.container !== null) {
                        if(this._o.columns) {
                            for(let i = 0; i < this.reviewCols().length; i++) {
                                this.reviewCols()[i].remove(item);
                            }
                        }
                        this.reviews.remove(item);
                    }
                } else {
                	Notifier.error('Ошибка');
				}
            },
            'json'
        );
	}

    /**
     * @param {object} data
     * @return {ReviewItem}
     */
	prependDataItem(data) {
		const item = this._prepareItems([data])[0];

        if(this.container !== null) {
            if(this._o.columns) {
                this.reviewCols()[0]().unshift(item);
            }
            this.reviews.unshift(item);
        }
		this._totalCount++;

		return item;
	}

	_initInfScroll() {
		this._infScroll = new InfiniteScroll(this._o.infScrollWrapper, {
			distance: this._o.infScrollDistance,
            onNeedMore: callback => {
                if (!this._loading()) {
                    this._loadReviews(callback);
                }
            }
		});
	}

	/**
	 *
	 * @return {JQuery.jqXHR}
	 * @private
	 */
	_loadReviews(callback = null) {
		this._loading(true);
		return this._xhr = $.get(
			siteUrl('reviews/get_ajax'),
			{
				...this._filters,
				page: ++this._page,
				calculate_average_rating: this._o.calculateAverageRating ? 1 : 0,
				include_sales: this._o.includeSales ? 1 : 0
			},
			null,
			'json'
		)
			.always(() => {
				this._loading(false);
				this._xhr = null;
				if(callback) {
					callback();
				}
			})
			.done(response => {
				const reviewItems = this._addItems(response.reviews);
				setTimeout(() => this._onLoad.fireWith(this, [reviewItems]));
				if(!response.hasMore && this._infScroll) {
                    this._infScroll.off();
				}
			});
	}

    /**
     * @param {array} items
	 * @return {array}
     * @private
     */
    _prepareItems(items) {
    	return items.map(r => {
    		r.parent = this;
            return new ReviewItem(r);
        });
	}

	/**
	 * @param {array} items
	 * @private
	 */
	_addItems(items) {
		const reviewItems = this._prepareItems(items);
		reviewItems.forEach(reviewItem => {
			reviewItem.onClick(this._handleItemClick);
		});

		this.reviews.pushAll(reviewItems);

		if (this._o.columns) {
			const reviewCols = [];
			for (let i = 0; i < this._o.columnsCount; ++i){
				reviewCols.push([]);
			}

			reviewItems.forEach(reviewItem => {
				if (this.colIndex >= this._o.columnsCount) {
					this.colIndex = 0;
				}
				reviewCols[this.colIndex++].push(reviewItem);
			});

			reviewCols.forEach((col, index) => {
				if (col.length) {
					this.reviewCols()[index].pushAll(col);
				}
			});
		}
		return reviewItems;
	}

	/**
	 * @param {ReviewItem} item
	 * @private
	 */
	_handleItemClick(item) {
		if(!this.isDetails) {
            if(item.data().is_question) {
                window.location = item.data().url;
            } else {
                const index = this.reviews().indexOf(item);
                MODAL_REVIEW.show(item.id, index, this._filters);
            }
        }
	}

    /**
	 *
     * @param {boolean} reinit
     * @private
     */
    static _reviewSizeSpot(reinit = false){
        let $items = $('.reviews-wrapper .review-item');
        if (!reinit) {
            $items = $items.not('[size]');
        }
        const width = $items.innerWidth();

        if(width > 240){
            $items.attr('size', 'large');
        } else if(width <= 240 && width >= 223){
            $items.attr('size', 'medium');
        } else if(width < 223){
            $items.attr('size', 'small');
        }
    }
}



// WEBPACK FOOTER //
// ./modules/reviews/common/model/reviewlist.js